import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import LoginIcon from '@mui/icons-material/Login';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { DoughnutGraph, GroupedBarGraph } from './Graphs';


// CustomCard overall mini cards with fully responsive text and fluid layout
export const CustomCard = ({ metric, gridProps }) => {
  const IconComp = metric.icon;
  const val = metric.value || 0;
  const lastVal = metric.lastValue || 0;
  const total = metric.total;
  
  const hasComparison = metric.lastValue !== undefined && metric.total !== undefined;
  
  const diff = val - lastVal;
  let percent = "0";
  if (lastVal > 0) {
    percent = Math.abs(Math.round((diff / lastVal) * 100));
  } else if (val > 0) {
    percent = "100";
  }

  // Display value: for Overall Statistics, the big number is total (e.g. 20, 86, 0, 2)
  const displayValue = total !== undefined ? total : val;
  const valLength = String(displayValue ?? '').length;

  // Responsive font size based on length of number so big numbers (e.g. 119190) never overflow on small screens / Mac
  const valueFontSize = valLength > 5
    ? { xs: '1.2rem', sm: '1.28rem', md: '1.2rem', lg: '1.38rem', xl: '1.65rem' }
    : valLength > 4
    ? { xs: '1.3rem', sm: '1.4rem', md: '1.3rem', lg: '1.5rem', xl: '1.75rem' }
    : { xs: '1.4rem', sm: '1.55rem', md: '1.45rem', lg: '1.65rem', xl: '1.85rem' };

  return (
    <Grid item xs={12} sm={6} md={3} {...(metric.gridProps || gridProps)}>
      <Link to={metric.link || "#"} style={{ textDecoration: "none", display: "block", height: "100%" }}>
        <Paper
          id={metric.id}
          elevation={0}
          sx={{
            p: { xs: '12px 14px', sm: '14px 15px', md: '13px 14px', lg: '15px 16px', xl: '16px 18px' },
            borderRadius: '14px',
            bgcolor: metric.cardBg || '#f8fafc',
            border: '1px solid rgba(226, 232, 240, 0.95)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: '#ffffff',
              boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-2px)',
              borderColor: 'rgba(203, 213, 225, 0.95)',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'left',
          }}
        >
          {/* Top Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.2, md: 1.1, lg: 1.3, xl: 1.6 }, textAlign: 'left' }}>
            {IconComp && (
              <Box
                sx={{
                  width: { xs: 36, sm: 40, md: 38, lg: 42, xl: 46 },
                  height: { xs: 36, sm: 40, md: 38, lg: 42, xl: 46 },
                  borderRadius: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: metric.bg || 'rgba(37, 99, 235, 0.08)',
                  flexShrink: 0,
                }}
              >
                <IconComp sx={{ color: metric.color || '#2563eb', fontSize: { xs: 18, sm: 20, md: 19, lg: 21, xl: 24 } }} />
              </Box>
            )}

            <Box sx={{ minWidth: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Tooltip title={metric.label || ''} arrow placement="top">
                <Typography
                  sx={{
                    fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.64rem', lg: '0.68rem', xl: '0.72rem' },
                    fontWeight: 700,
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    lineHeight: 1.2,
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {metric.label}
                </Typography>
              </Tooltip>

              {metric.userName && (
                <Typography
                  sx={{
                    fontSize: { xs: '0.74rem', sm: '0.78rem', md: '0.75rem', lg: '0.8rem', xl: '0.85rem' },
                    fontWeight: 600,
                    color: '#1e293b',
                    mt: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {metric.userName}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, lg: 1.2 }, mt: 0.3, textAlign: 'left', flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: valueFontSize,
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1,
                    textAlign: 'left',
                  }}
                >
                  {displayValue}
                </Typography>

                {hasComparison && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      px: { xs: '5px', sm: '6px', lg: '7px' },
                      py: '1.5px',
                      borderRadius: '20px',
                      fontSize: { xs: '0.62rem', sm: '0.65rem', lg: '0.7rem' },
                      fontWeight: 700,
                      bgcolor: diff > 0 ? '#dcfce7' : diff < 0 ? '#fee2e2' : '#f1f5f9',
                      color: diff > 0 ? '#15803d' : diff < 0 ? '#b91c1c' : '#334155',
                      flexShrink: 0,
                    }}
                  >
                    {diff > 0 ? `↑ ${percent}%` : diff < 0 ? `↓ ${percent}%` : `• 0%`}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Bottom Row / Comparison */}
          {hasComparison ? (
            <Box
              sx={{
                mt: { xs: 1, sm: 1.2, lg: 1.4 },
                pt: 0.8,
                borderTop: '1px solid rgba(241, 245, 249, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '3px 8px',
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.64rem', sm: '0.68rem', md: '0.66rem', lg: '0.7rem', xl: '0.74rem' },
                  color: '#475569',
                  fontWeight: 550,
                  whiteSpace: 'nowrap',
                }}
              >
                This Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{val}</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.64rem', sm: '0.68rem', md: '0.66rem', lg: '0.7rem', xl: '0.74rem' },
                  color: '#475569',
                  fontWeight: 550,
                  whiteSpace: 'nowrap',
                }}
              >
                Last Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{lastVal}</span>
              </Typography>
            </Box>
          ) : metric.valueLabel ? (
            <Box sx={{ mt: 1, textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.74rem', lg: '0.78rem' },
                  color: '#475569',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                {metric.valueLabel}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Link>
    </Grid>
  );
};

// SectionHeader component 
export const SectionHeader = ({ title, action, mt = 5 }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ height: 24, width: 4, bgcolor: "primary.main", borderRadius: "999px" }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

// graphical cards for all 3 graphs
export const SummarySection = ({ 
  controls, 
  chart, 
  stats, 
  summaryTitle, 
  footerNote, 
  footerIconColor = "warning.main",
  chartColSize = 5,
  statsColSize = 7,
  paperSx = { p: 3, borderRadius: 2, boxShadow: 1, borderColor: "divider" }
}) => {
  return (
    <>
      <Box>
        <Paper variant="outlined" sx={paperSx}>
          {controls && <Box sx={{ mb: 3, display: "flex", justifyContent: "left" }}>{controls}</Box>}
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={chartColSize}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                {chart}
              </Box>
            </Grid>
            <Grid item xs={12} md={statsColSize}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}>
                  {summaryTitle}
                </Typography>

                <Grid container spacing={2}>
                  {stats.map((stat, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: stat.bg }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {stat.label}
                          </Typography>
                          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: stat.color, lineHeight: 1.2, mt: 0.3 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {footerNote && (
                  <Box sx={{ mt: 1, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography component="div" variant="body2" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 4, height: 16, bgcolor: footerIconColor, borderRadius: 1 }} />
                      {footerNote}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

// Generic widget container card with subtle border and elevation
export const DashboardWidgetCard = ({ children, sx = {}, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: '14px 16px', sm: '16px 18px', md: '16px 18px', lg: '18px 20px', xl: '20px 22px' },
      borderRadius: '16px',
      bgcolor: '#ffffff',
      border: '1px solid rgba(226, 232, 240, 0.85)',
      boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'left',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

// Shared Widget Header
export const WidgetHeader = ({ title, action, mb = 1.5, titleSx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb, gap: 1 }}>
    <Typography sx={{ fontSize: { xs: '0.84rem', sm: '0.88rem', lg: '0.95rem' }, fontWeight: 700, color: '#0f172a', ...titleSx }}>
      {title}
    </Typography>
    {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
  </Box>
);

// Shared Timeframe Dropdown Select
export const WidgetTimeframeSelect = ({
  value,
  onChange,
  minWidth = 110,
  options = [
    { value: 'today', label: 'Today' },
    { value: 'monthly', label: 'This Month' },
    { value: 'overall', label: 'Overall' },
  ],
}) => (
  <Select
    value={value}
    onChange={(e) => onChange && onChange(e.target.value)}
    size="small"
    sx={{
      minWidth: { xs: 90, sm: minWidth },
      height: { xs: 28, sm: 30 },
      fontSize: { xs: '0.72rem', sm: '0.76rem', lg: '0.78rem' },
      bgcolor: '#f8fafc',
      borderRadius: '8px',
      borderColor: '#e2e8f0',
      fontWeight: 600,
      '& .MuiSelect-select': { py: { xs: '2px', sm: '3px' }, px: { xs: '8px', sm: '10px' } },
    }}
  >
    {options.map((opt) => (
      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' } }}>
        {opt.label}
      </MenuItem>
    ))}
  </Select>
);

// Row 3 - Left: Work Report Widget (Vertical List like Today's Activity)
export const WorkReportWidget = ({
  performanceData = { topUser: {}, minUser: {} },
  approverPerformanceData = { topUser: {}, minUser: {} },
  timeframe = "today",
  onTimeframeChange,
}) => {
  const reportItems = [
    {
      label: "Top Calibrator",
      userName: performanceData?.topUser?.name || "N/A",
      value: performanceData?.topUser?.count || 0,
      color: "#16a34a",
      bg: "rgba(22, 163, 74, 0.08)",
      icon: EmojiEventsIcon,
    },
    {
      label: "Lowest Calibrator",
      userName: performanceData?.minUser?.name || "N/A",
      value: performanceData?.minUser?.count || 0,
      color: "#ea580c",
      bg: "rgba(234, 88, 12, 0.08)",
      icon: GroupIcon,
    },
    {
      label: "Top Approver",
      userName: approverPerformanceData?.topUser?.name || "N/A",
      value: approverPerformanceData?.topUser?.count || 0,
      color: "#2563eb",
      bg: "rgba(37, 99, 235, 0.08)",
      icon: WorkspacePremiumIcon,
    },
    {
      label: "Lowest Approver",
      userName: approverPerformanceData?.minUser?.name || "N/A",
      value: approverPerformanceData?.minUser?.count || 0,
      color: "#9333ea",
      bg: "rgba(147, 51, 234, 0.08)",
      icon: GroupIcon,
    },
  ];

  return (
    <DashboardWidgetCard>
      <WidgetHeader
        title="Work Report"
        mb={1.8}
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Activity List - Clean rows matching Today's Activity */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, flexGrow: 1, justifyContent: 'space-around' }}>
        {reportItems.map((item, idx) => {
          const IconComp = item.icon;
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: '7px',
                px: '8px',
                borderRadius: '8px',
                transition: 'background-color 150ms ease',
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.2 }, minWidth: 0 }}>
                <Box
                  sx={{
                    width: { xs: 26, sm: 28, lg: 32 },
                    height: { xs: 26, sm: 28, lg: 32 },
                    borderRadius: '8px',
                    bgcolor: item.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComp sx={{ color: item.color, fontSize: { xs: 15, sm: 16, lg: 18 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.76rem', lg: '0.82rem' }, fontWeight: 650, color: '#1e293b', lineHeight: 1.2 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.74rem' },
                      fontWeight: 500,
                      color: '#64748b',
                      mt: 0.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: 85, sm: 110, md: 100, lg: 140 },
                    }}
                  >
                    {item.userName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.82rem', sm: '0.88rem', lg: '0.94rem' },
                    fontWeight: 750,
                    color: item.color,
                  }}
                >
                  {item.value || 0}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 1 - Left: Calibration Trend (Grouped Bar Chart)
export const CalibrationTrendWidget = ({
  data,
  timeframe = "3months",
  onTimeframeChange,
}) => {
  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <span>Calibration Trend</span>
            <Tooltip
              title="This calibration trend shows the progress of an inward, from creation to final approval. An inward remains Pending while calibration is in progress and moves to Completed once inward is approved."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  transition: 'color 150ms ease',
                  '&:hover': { color: '#475569' },
                }}
              />
            </Tooltip>
          </Box>
        }
        mb={1.2}
        action={
          <WidgetTimeframeSelect
            value={timeframe}
            onChange={onTimeframeChange}
            minWidth={120}
            options={[
              { value: '3months', label: 'Last 3 Months' },
              { value: '6months', label: 'Last 6 Months' },
              { value: 'year', label: 'This Year' },
            ]}
          />
        }
      />

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.2, mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Rejected</Typography>
        </Box>
      </Box>

      {/* Grouped Bar Chart */}
      <Box sx={{ height: 185, width: '100%', mt: 'auto' }}>
        <GroupedBarGraph data={data} />
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 1 - Center: Certificate Status (Donut Chart with central total and legend)
export const CertificateStatusWidget = ({
  stats = {
    total: 0,
    calibrated: 0,
    uncalibrated: 0,
    approved: 0,
    pendingApproval: 0,
    rejected: 0,
    amendment: 0,
  },
  timeframe = "today",
  onTimeframeChange,
}) => {
  const total = Number(stats.total) || 0;
  const calibrated = Number(stats.calibrated) || 0;
  const uncalibrated = Number(stats.uncalibrated) || 0;
  const approved = Number(stats.approved) || 0;
  const pendingApproval = Number(stats.pendingApproval ?? stats.pending) || 0;
  const rejected = Number(stats.rejected) || 0;
  const amendment = Number(stats.amendment) || 0;

  const doughnutData = {
    labels: total > 0 ? ['Approved', 'Pending for Approval', 'Uncalibrated', 'Rejected'] : [],
    datasets: [
      {
        data: total > 0 ? [approved, pendingApproval, uncalibrated, rejected] : [1],
        backgroundColor: total > 0
          ? ['#22c55e', '#f59e0b', '#94a3b8', '#ef4444']
          : ['#e2e8f0'],
        borderWidth: 0,
        hoverOffset: total > 0 ? 4 : 0,
      },
    ],
  };

  const statusItems = [
    { label: 'Calibrated', count: calibrated, color: '#0284c7' },
    { label: 'Uncalibrated', count: uncalibrated, color: '#94a3b8' },
    { label: 'Approved', count: approved, color: '#22c55e' },
    { label: 'Pending for Approval', count: pendingApproval, color: '#f59e0b' },
    { label: 'Rejected', count: rejected, color: '#ef4444' },
    { label: 'Amendment', count: amendment, color: '#a855f7' },
  ];

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <span>Certificate Status</span>
            <Tooltip
              title="Shows the current status of certificates based on their entry date. The counts include certificates created within the selected period, such as Today, This Month, or Overall."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  transition: 'color 150ms ease',
                  '&:hover': { color: '#475569' },
                }}
              />
            </Tooltip>
          </Box>
        }
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Donut Chart & Legend */}
      <Grid container spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
        {/* Left Donut */}
        <Grid item xs={12} sm={5} md={5}>
          <Box sx={{
            position: 'relative',
            width: { xs: 136, sm: 146, md: 142, lg: 152, xl: 160 },
            height: { xs: 136, sm: 146, md: 142, lg: 152, xl: 160 },
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DoughnutGraph data={doughnutData} cutout="78%" />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 1,
                maxWidth: '74%',
                width: '74%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: String(total).length >= 8
                    ? { xs: '0.72rem', sm: '0.76rem', lg: '0.82rem' }
                    : String(total).length >= 7
                    ? { xs: '0.76rem', sm: '0.82rem', lg: '0.88rem' }
                    : String(total).length >= 6
                    ? { xs: '0.84rem', sm: '0.90rem', lg: '0.98rem' }
                    : String(total).length >= 5
                    ? { xs: '0.98rem', sm: '1.06rem', lg: '1.16rem' }
                    : { xs: '1.20rem', sm: '1.30rem', lg: '1.42rem' },
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1.1,
                  letterSpacing: String(total).length >= 7 ? '-0.7px' : String(total).length >= 6 ? '-0.5px' : 'normal',
                  whiteSpace: 'nowrap',
                }}
              >
                {total}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.60rem', sm: '0.64rem', lg: '0.68rem' }, fontWeight: 600, color: '#64748b', mt: 0.2 }}>
                Total
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Status Breakdown */}
        <Grid item xs={12} sm={7} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.45, sm: 0.6, lg: 0.75 }, pl: { md: 0.5, lg: 1 } }}>
            {statusItems.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '1px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                  <Box
                    sx={{
                      width: { xs: 7, sm: 8, lg: 9 },
                      height: { xs: 7, sm: 8, lg: 9 },
                      borderRadius: '2.5px',
                      bgcolor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.7rem', lg: '0.75rem' },
                      fontWeight: 600,
                      color: '#334155',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '0.72rem', sm: '0.76rem', md: '0.74rem', lg: '0.8rem' },
                    fontWeight: 700,
                    color: '#0f172a',
                    lineHeight: 1.2,
                    ml: 1,
                  }}
                >
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </DashboardWidgetCard>
  );
};

// Row 1 - Right: Discipline Wise Calibration (Horizontal bars)
export const DisciplineWiseWidget = ({
  disciplines = [],
  timeframe = "today",
  onTimeframeChange,
}) => {
  const items = disciplines || [];
  const maxCount = Math.max(...items.map((i) => i.count || 0), 1);

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title="Discipline Wise Calibration"
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Progress Bars List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1, justifyContent: items.length === 0 ? 'center' : 'space-around' }}>
        {items.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center', py: 4 }}>
            No discipline calibration data found
          </Typography>
        ) : (
          items.map((item, idx) => {
            const widthPct = Math.max(Math.round(((item.count || 0) / maxCount) * 100), item.count > 0 ? 8 : 0);
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '4px 6px',
                  borderRadius: '8px',
                  transition: 'background-color 150ms ease',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <Tooltip title={item.name} arrow placement="top">
                  <Typography
                    sx={{
                      width: { xs: 85, sm: 110, md: 100, lg: 125, xl: 150 },
                      fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.7rem', lg: '0.76rem' },
                      fontWeight: 600,
                      color: '#334155',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'default',
                    }}
                  >
                    {item.name}
                  </Typography>
                </Tooltip>

                <Box sx={{ flex: 1, height: { xs: 6, sm: 7, lg: 8 }, borderRadius: '4px', bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${widthPct}%`,
                      height: '100%',
                      borderRadius: '4px',
                      bgcolor: item.color || '#0284c7',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    minWidth: 24,
                    textAlign: 'right',
                    fontSize: { xs: '0.74rem', sm: '0.78rem', lg: '0.82rem' },
                    fontWeight: 750,
                    color: item.count > 0 ? '#0f172a' : '#94a3b8',
                    flexShrink: 0,
                  }}
                >
                  {item.count || 0}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </DashboardWidgetCard>
  );
};



// Row 2 - Right: Today's Activity (Side-by-side cards)
export const TodayActivityWidget = ({
  counts = {
    inwards: 0,
    calibrated: 0,
    datasheets: 0,
    approved: 0,
  },
  hasAccessToModule,
}) => {
  const activities = [
    { moduleId: 3, label: 'Inwards Created', value: counts.inwards, icon: MoveToInboxOutlinedIcon, color: '#2563eb', bg: 'rgba(37, 99, 235, 0.08)', link: '/inward' },
    { moduleId: 4, label: 'Calibrated', value: counts.calibrated ?? counts.datasheets, icon: TuneOutlinedIcon, color: '#0284c7', bg: 'rgba(2, 132, 199, 0.08)', link: '/datasheet' },
    { moduleId: 5, label: 'Approved Certificates', value: counts.approved, icon: WorkspacePremiumOutlinedIcon, color: '#16a34a', bg: 'rgba(22, 163, 74, 0.08)', link: '/certificate' },
  ].filter(act => !act.moduleId || (hasAccessToModule ? hasAccessToModule(act.moduleId) : true));

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <span>Today's Activity</span>
            <Tooltip
              title="Shows all certificate and inward activities completed today, regardless of when the inward / certificates was created."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  transition: 'color 150ms ease',
                  '&:hover': { color: '#475569' },
                }}
              />
            </Tooltip>
          </Box>
        }
        mb={2}
      />

      {/* 3 Items Side-by-side (Icons on top, Count & Label below) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: { xs: 1, sm: 1.5 },
          flexGrow: 1,
          alignItems: 'center',
        }}
      >
        {activities.map((act, idx) => {
          const IconComp = act.icon;
          return (
            <Link
              key={idx}
              to={act.link || "#"}
              style={{ textDecoration: 'none', display: 'flex', flex: 1, minWidth: 0, height: '100%' }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: { xs: '12px 4px', sm: '16px 6px', md: '18px 8px' },
                  borderRadius: '12px',
                  bgcolor: '#ffffff',
                  border: '1px solid #f1f5f9',
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 36, sm: 42, lg: 44 },
                    height: { xs: 36, sm: 42, lg: 44 },
                    borderRadius: '12px',
                    bgcolor: act.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 0.8, sm: 1.2 },
                    flexShrink: 0,
                  }}
                >
                  <IconComp sx={{ color: act.color, fontSize: { xs: 19, sm: 22, lg: 24 } }} />
                </Box>

                {/* Count below icon */}
                <Typography
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.38rem', md: '1.3rem', lg: '1.5rem' },
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1.1,
                    mb: { xs: 0.4, sm: 0.6 },
                  }}
                >
                  {act.value || 0}
                </Typography>

                {/* Label below count */}
                <Typography
                  sx={{
                    fontSize: { xs: '0.64rem', sm: '0.72rem', md: '0.7rem', lg: '0.76rem' },
                    fontWeight: 600,
                    color: '#64748b',
                    lineHeight: 1.25,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  {act.label}
                </Typography>
              </Box>
            </Link>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 3 - Left: Recent Inwards Table (Latest 5)
export const RecentInwardsWidget = ({ inwards = [] }) => {
  const rows = inwards || [];

  const getStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('complete') || s === '1') {
      return { color: '#16a34a', dot: '#16a34a', label: 'Completed' };
    }
    if (s.includes('pending') || s === '0') {
      return { color: '#d97706', dot: '#d97706', label: 'Pending' };
    }
    return { color: '#2563eb', dot: '#2563eb', label: 'In Progress' };
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 16px', sm: '16px 18px' } }}>
      {/* Header */}
      <WidgetHeader
        title="Recent Inwards"
        titleSx={{ fontSize: '0.88rem' }}
        mb={1}
        action={
          <Link to="/inward" style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
              View All
            </Typography>
          </Link>
        }
      />

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '7px !important',
              px: '6px !important',
            },
          }}
        >
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.68rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '6px !important', sm: '8px !important' }, pl: { xs: '8px !important', sm: '10px !important' } }}>Inward No</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.68rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '6px !important', sm: '8px !important' } }}>Client</TableCell>
              <TableCell align="center" sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.68rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '6px !important', sm: '8px !important' } }}>Inst.</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.68rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '6px !important', sm: '8px !important' } }}>Date</TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.68rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '6px !important', sm: '8px !important' }, pr: { xs: '8px !important', sm: '10px !important' } }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: '0.78rem' }}>
                  No recent inwards found
                </TableCell>
              </TableRow>
            ) : (
              rows.slice(0, 5).map((row, index) => {
              const status = getStatusStyle(row.status);
              return (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&:last-child td': { borderBottom: 'none !important' },
                  }}
                >
                  <TableCell sx={{ pl: { xs: '8px !important', sm: '10px !important' }, maxWidth: { xs: 80, sm: 95, lg: 110 } }}>
                    <Tooltip title={row.inwardNo} arrow placement="top">
                      <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.71rem', lg: '0.72rem' }, fontWeight: 650, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.inwardNo}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: { xs: 80, sm: 95, lg: 110 } }}>
                    <Tooltip title={row.client} arrow placement="top">
                      <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.71rem', lg: '0.72rem' }, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.client}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: '0.68rem', sm: '0.71rem', lg: '0.72rem' }, fontWeight: 600, color: '#334155' }}>
                    {row.totalInstruments}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.66rem', sm: '0.68rem', lg: '0.7rem' }, color: '#64748b', whiteSpace: 'nowrap' }}>
                    {row.date}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: { xs: '8px !important', sm: '10px !important' } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: status.dot }} />
                      <Typography sx={{ fontSize: { xs: '0.66rem', sm: '0.69rem', lg: '0.7rem' }, fontWeight: 600, color: status.color, whiteSpace: 'nowrap' }}>
                        {status.label}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            }))}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};

// Row 4 - Lowest: Upcoming Due Dates Table (Full Width)
export const UpcomingDueDatesWidget = ({ dueDates = [] }) => {
  const rows = dueDates || [];

  const getUrgencyColor = (days) => {
    const d = Number(days);
    if (d <= 5) return '#dc2626';
    if (d <= 10) return '#ea580c';
    return '#16a34a';
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '16px 18px', sm: '20px 22px' } }}>
      {/* Header */}
      <WidgetHeader
        title="Upcoming Due Dates"
        titleSx={{ fontSize: '0.95rem' }}
        mb={1.5}
        action={
          <Link to="/scheduler" style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
              View All
            </Typography>
          </Link>
        }
      />

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '8px !important',
              px: '10px !important',
            },
          }}
        >
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' }, pl: { xs: '8px !important', sm: '12px !important' } }}>
                Client
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                Instrument Name
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                Range
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                LC
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                DUC ID
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                Serial No.
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                Date of Calibration
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' } }}>
                Due Date
              </TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.64rem', sm: '0.67rem', lg: '0.7rem' }, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: { xs: '7px !important', sm: '9px !important' }, pr: { xs: '8px !important', sm: '12px !important' } }}>
                Days Left
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: '0.8rem' }}>
                  No upcoming due dates found
                </TableCell>
              </TableRow>
            ) : (
              rows.slice(0, 5).map((row, index) => {
                const uColor = getUrgencyColor(row.daysLeft);
                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&:last-child td': { borderBottom: 'none !important' },
                    }}
                  >
                    <TableCell sx={{ pl: { xs: '8px !important', sm: '12px !important' } }}>
                      <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem', lg: '0.75rem' }, fontWeight: 650, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {row.client || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem', lg: '0.74rem' }, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                        {row.instrumentName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, color: '#475569', whiteSpace: 'nowrap' }}>
                        {row.range || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, color: '#475569', whiteSpace: 'nowrap' }}>
                        {row.lc || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, fontWeight: 600, color: '#0284c7', whiteSpace: 'nowrap' }}>
                        {row.ducId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, color: '#475569', whiteSpace: 'nowrap' }}>
                        {row.serialNo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.calibDate || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.67rem', sm: '0.7rem', lg: '0.73rem' }, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.dueDate || '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: '12px !important' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.2,
                          py: 0.3,
                          borderRadius: '12px',
                          bgcolor: `${uColor}15`,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.73rem', fontWeight: 700, color: uColor, whiteSpace: 'nowrap' }}>
                          {row.daysLeft}d left
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};
